import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RadioGroup, Switch } from '../ui';
import SettingsGroup from '../admin/SettingsGroup';

const STORAGE_KEY = 'etch.preferences';

const DEFAULTS = {
  notifyDue: true,
  notifyOnboarding: true,
  reduceMotion: false,
  highContrast: false
};

function readPreferences() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS };
  } catch {
    return { ...DEFAULTS };
  }
}

/**
 * Personal preferences that are not the theme: language, notifications and the
 * accessibility switches (spec §10.6).
 *
 * The two accessibility preferences are mirrored onto `<html>` as
 * `data-reduce-motion` / `data-contrast` so the stylesheets can honour an
 * explicit user choice on top of the `prefers-reduced-motion` media query — a
 * user on a machine where the OS setting is not available still needs a way to
 * turn decorative motion off.
 */
export function usePreferences() {
  const [preferences, setPreferences] = useState(readPreferences);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch {
      /* private mode — the session still works, only persistence is lost */
    }
    const root = document.documentElement;
    root.dataset.reduceMotion = preferences.reduceMotion ? 'true' : 'false';
    root.dataset.contrast = preferences.highContrast ? 'high' : 'normal';
  }, [preferences]);

  const update = useCallback((patch) => setPreferences((current) => ({ ...current, ...patch })), []);

  return [preferences, update];
}

/** Language picker — `i18n` persists the choice to `etch.language` itself. */
export function LanguageSettings() {
  const { t, i18n } = useTranslation();
  const [saved, setSaved] = useState(false);

  function change(next) {
    i18n.changeLanguage(next);
    setSaved(true);
  }

  return (
    <SettingsGroup title={t('settings.language')} description={t('settings.languageDesc')}>
      <RadioGroup
        name="etch-language"
        legend={t('settings.language')}
        value={i18n.resolvedLanguage || i18n.language}
        onChange={change}
        options={[
          { value: 'de', label: t('settings.languageGerman') },
          { value: 'en', label: t('settings.languageEnglish') }
        ]}
      />
      <p role="status" aria-live="polite" className="etch-meta">
        {saved ? t('settings.saved') : ''}
      </p>
    </SettingsGroup>
  );
}

/** Notification preferences. Local-only until a push/e-mail backend exists. */
export function NotificationSettings() {
  const { t } = useTranslation();
  const [preferences, update] = usePreferences();

  return (
    <SettingsGroup title={t('settings.notifications')} description={t('settings.notificationsDesc')}>
      <Switch
        label={t('settings.notifyDue')}
        checked={preferences.notifyDue}
        onChange={(event) => update({ notifyDue: event.target.checked })}
      />
      <Switch
        label={t('settings.notifyOnboarding')}
        checked={preferences.notifyOnboarding}
        onChange={(event) => update({ notifyOnboarding: event.target.checked })}
      />
      <p className="etch-meta">{t('settings.notificationsScope')}</p>
    </SettingsGroup>
  );
}

/** Accessibility preferences (spec §12). */
export function AccessibilitySettings() {
  const { t } = useTranslation();
  const [preferences, update] = usePreferences();

  return (
    <SettingsGroup
      title={t('settings.accessibility.title')}
      description={t('settings.accessibility.desc')}
    >
      <Switch
        label={t('settings.accessibility.reduceMotion')}
        checked={preferences.reduceMotion}
        onChange={(event) => update({ reduceMotion: event.target.checked })}
      />
      <Switch
        label={t('settings.accessibility.highContrast')}
        checked={preferences.highContrast}
        onChange={(event) => update({ highContrast: event.target.checked })}
      />
      <p className="etch-meta">{t('settings.accessibility.systemHint')}</p>
    </SettingsGroup>
  );
}
